import { useEffect, useCallback } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getToken, clearToken, formatApiError } from "../../lib/api";
import LacBadge from "../lac/LacBadge";

const TABS = [
  { to: "/admin/dashboard", label: "Bookings", id: "bookings" },
  { to: "/admin/schedule", label: "Schedule", id: "schedule" },
  { to: "/admin/customers", label: "Customers", id: "customers" },
];

export const inputCls =
  "w-full bg-[#0d0d0d] border border-white/10 rounded-sm px-3 py-2 text-white text-sm placeholder:text-zinc-600 focus:border-[#D946EF] focus:outline-none";

// Redirects to login when there is no token; returns an error handler that also catches 401s.
export function useAdminGuard() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!getToken()) navigate("/admin/login");
  }, [navigate]);
  return useCallback((err) => {
    if (err?.response?.status === 401) {
      clearToken();
      navigate("/admin/login");
      return;
    }
    toast.error(formatApiError(err));
  }, [navigate]);
}

export default function AdminHeader({ onRefresh, loading }) {
  const navigate = useNavigate();
  const logout = () => {
    clearToken();
    toast.success("Signed out");
    navigate("/admin/login");
  };
  return (
    <header className="glass border-b border-white/5 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3">
          <LacBadge size={44} />
          <div className="leading-tight">
            <div className="text-[10px] tracking-[0.35em] text-zinc-400">LAC · CONTROL</div>
            <div className="font-display font-bold tracking-tight text-chrome">Admin</div>
          </div>
        </Link>
        <nav className="flex items-center gap-1 p-1 rounded-full border border-white/10 bg-[#0a0a0a]" data-testid="admin-nav">
          {TABS.map((t) => (
            <NavLink
              key={t.id}
              to={t.to}
              data-testid={`admin-nav-${t.id}`}
              className={({ isActive }) =>
                `px-4 py-1.5 rounded-full text-sm transition-colors ${isActive ? "bg-[#D946EF] text-white" : "text-zinc-400 hover:text-white"}`
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          {onRefresh && (
            <button onClick={onRefresh} data-testid="refresh-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-300 hover:border-[#D946EF]/40 hover:text-[#D946EF] transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          )}
          <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-300 hover:border-red-500/40 hover:text-red-400 transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
